/**
 * Normalizes login / signup verify OTP API bodies (flat vs nested access_token, profile flags).
 */

function pickJwt(value: unknown): string | null {
  return typeof value === 'string' && value.split('.').length === 3 ? value : null;
}

function pickTokenFromAuthObject(source: unknown): string | null {
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return null;
  }
  const record = source as Record<string, unknown>;
  const direct = pickJwt(record['access_token']);
  if (direct) {
    return direct;
  }
  const legacyWrap = record['access_token'];
  if (legacyWrap && typeof legacyWrap === 'object' && !Array.isArray(legacyWrap)) {
    const nested = (legacyWrap as Record<string, unknown>)['data'];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return pickJwt((nested as Record<string, unknown>)['access_token']);
    }
  }
  return null;
}

export function extractAccessTokenFromAuthBody(body: any): string | null {
  if (!body || typeof body !== 'object') {
    return null;
  }
  const fromRoot = pickTokenFromAuthObject(body);
  if (fromRoot) {
    return fromRoot;
  }
  return pickTokenFromAuthObject(body.data);
}

/**
 * Collects is_profile_completed / is_verified from typical auth API shapes (root, data, nested data, user).
 * Defaults to false when absent.
 */
export function readAuthProfileFlags(apiBody: any): {
  is_profile_completed: boolean;
  is_verified: boolean;
} {
  const roots: any[] = [];
  if (apiBody && typeof apiBody === 'object') roots.push(apiBody);
  const d = apiBody?.data;
  if (d && typeof d === 'object' && !Array.isArray(d)) roots.push(d);
  const inner = d?.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) roots.push(inner);

  let completed: boolean | undefined;
  let verified: boolean | undefined;

  const asBool = (v: unknown): boolean => {
    if (v === true || v === 'true' || v === 1 || v === '1') return true;
    if (v === false || v === 'false' || v === 0 || v === '0') return false;
    return Boolean(v);
  };

  const tryRead = (o: any) => {
    if (!o || typeof o !== 'object') return;
    if (completed === undefined) {
      if ('is_profile_completed' in o) completed = asBool(o.is_profile_completed);
      else if ('isProfileCompleted' in o) completed = asBool(o.isProfileCompleted);
    }
    if (verified === undefined) {
      if ('is_verified' in o) verified = asBool(o.is_verified);
      else if ('isVerified' in o) verified = asBool(o.isVerified);
    }
  };

  for (const r of roots) {
    tryRead(r);
    if (r?.user && typeof r.user === 'object') tryRead(r.user);
  }

  return {
    is_profile_completed: completed ?? false,
    is_verified: verified ?? false,
  };
}
